import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import '../styles/ImageModal.css';

interface ImageModalProps {
  isOpen: boolean;
  imageSrc: string;
  imageAlt: string;
  onClose: () => void;
}

const ImageModal: React.FC<ImageModalProps> = ({ isOpen, imageSrc, imageAlt, onClose }) => {
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleBodyScroll = () => {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'unset';
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      handleBodyScroll();
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="image-modal-overlay" 
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="image-modal-container">
        <button
          className="image-modal-close"
          onClick={onClose}
          aria-label="Close image"
          type="button"
        >
          <X className="close-icon" />
        </button>
        
        <div className="image-modal-content">
          <img
            src={imageSrc}
            alt={imageAlt}
            className="modal-image"
            loading="lazy"
          />
          
          <div className="image-modal-caption">
            <h3 id="modal-title">{imageAlt}</h3>
            <p className="modal-instructions">
              Click outside the image or press ESC to close
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageModal;